/**
 * Binds the 2-step SMS-only login wizard in index.html to the auth service.
 *
 * Wizard structure (handled here):
 *   step 1 — phone
 *   step 2 — terms checkbox + final submit (#authButton) → triggers SMS OTP
 *
 * After OTP success, the user is logged in WITHOUT a vehicle. The home
 * screen's search will gate them through `searchGate.requireVehicleOrPrompt`
 * to register a vehicle when they first attempt one.
 *
 * Legacy 3-step compat: the plate/year inputs from the old flow can still
 * be present in the DOM (e.g. PWA install-session autofill). `prefillFromLocalState`
 * + `autoSubmit` still route through the legacy `registerOrLogin` for that
 * trusted-device path, preserving full backwards compatibility.
 */

import * as authService from './auth.service.js';
import { createLogger } from '../../core/logger.js';
import * as alert from '../../core/alert.js';
import * as spinner from '../../core/spinner.js';
import { isValidMobile, normalize as normalizePhone } from '../../shared/phone.js';
import { isValid as isValidPlate } from '../../shared/plate.js';
import { LocalState } from '../../core/localState.js';
import { Auth } from '../../app/config.js';
import { isDeviceTrustedForPhone } from './device.service.js';
import { showBlockedAccountModal } from '../../shared/blockedAccountModal.js';

const log = createLogger('login.view');

const IDS = /** @type {const} */ ({
  authButton: 'authButton',
  loginPhone: 'loginPhone',
  plate: 'plate',
  carProductionYear: 'carProductionYear',
  termsCheckbox: 'termsCheckbox',
  loginAlert: 'loginAlert',
  // Wizard-specific
  stepCounter: 'loginStepCounter',
  yearGrid: 'loginYearGrid',
  yearValue: 'loginYearValue',
  summaryPhone: 'loginSummaryPhone',
  summaryPlate: 'loginSummaryPlate',
  summaryYear: 'loginSummaryYear',
});

const MIN_YEAR = 1980;

let wired = false;
let currentStep = 1;

/**
 * Populate the production-year dropdown with values from current year down
 * to MIN_YEAR. Safe to call multiple times — clears existing options first.
 */
export function populateYearSelect() {
  const select = /** @type {HTMLSelectElement | null} */ (document.getElementById(IDS.carProductionYear));
  if (!select) {
    log.warn('carProductionYear select not found');
    return;
  }
  if (select.dataset.populated === 'true') return;
  select.innerHTML = '<option value="" disabled selected>בחר שנה</option>';
  const currentYear = new Date().getFullYear();
  for (let year = currentYear; year >= MIN_YEAR; year--) {
    const option = document.createElement('option');
    option.value = String(year);
    option.textContent = String(year);
    select.appendChild(option);
  }
  select.dataset.populated = 'true';
}

let submitting = false;

/** Attach the submit handler + wizard navigation. Idempotent. */
export function wireForm() {
  if (wired) return;
  const button = document.getElementById(IDS.authButton);
  if (!button) {
    log.warn('authButton not found — login form not wired');
    return;
  }
  button.addEventListener('click', onSubmit);
  wireWizard();
  wirePlateAutoFormat();
  wireYearChips();
  wired = true;
  log.info('login form wired');
}

// ====================================================================
// Wizard navigation
// ====================================================================

/**
 * Hook step transitions: [data-wiz-next] / [data-wiz-back] anywhere in
 * #loginContainer trigger goToStep() with validation per-step.
 */
function wireWizard() {
  const container = document.getElementById('loginContainer');
  if (!container) return;
  container.addEventListener('click', async (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    const nextBtn = t.closest?.('[data-wiz-next]');
    if (nextBtn) {
      const target = Number(/** @type {HTMLElement} */ (nextBtn).getAttribute('data-wiz-next'));
      const ok = await validateBeforeAdvance(currentStep);
      if (ok && target) goToStep(target);
      return;
    }
    const backBtn = t.closest?.('[data-wiz-back]');
    if (backBtn) {
      const target = Number(/** @type {HTMLElement} */ (backBtn).getAttribute('data-wiz-back'));
      if (target) {
        clearAlert();
        goToStep(target);
      }
    }
  });
  // Initialise to step 1
  goToStep(1, /*silent*/ true);
}

/**
 * @param {1 | 2 | 3 | number} step
 * @param {boolean} [silent]   when true, skip side effects (focus, scroll)
 */
function goToStep(step, silent = false) {
  currentStep = step;
  // Toggle panes
  document.querySelectorAll('#loginContainer .lw-pane').forEach((el) => {
    el.classList.toggle('active', el.getAttribute('data-pane') === String(step));
  });
  // Update progress bar
  document.querySelectorAll('#loginContainer .lw-step').forEach((el) => {
    const n = Number(el.getAttribute('data-step'));
    el.classList.toggle('lw-done', n < step);
    el.classList.toggle('lw-current', n === step);
  });
  const counter = document.getElementById(IDS.stepCounter);
  if (counter) counter.textContent = `${step}/2`;

  // Populate summary on entering the terms/confirm step. The new SMS-only
  // wizard is 2 steps (was 3) — step 2 is now the terms+submit step that
  // shows the phone summary. Keep the step-3 case as a fallback for any
  // future expansion.
  if (step === 2 || step === 3) populateSummary();

  if (!silent) {
    // Per-step focus / scroll behaviour:
    //   Step 1  — phone input (already focused via the landing CTA, but
    //             refocus is harmless on direct entry).
    //   Step 2  — plate input. The year picker is chip-based (no input)
    //             so the keyboard popping is desired only for the plate.
    //   Step 3  — no text input; bring the primary action button (#authButton)
    //             into view so the terms checkbox + submit button are visible.
    // The global focusin / visualViewport handlers in index.html take it
    // from here — they scroll the focused input above the keyboard.
    setTimeout(() => {
      let toFocus = null;
      if (step === 1) toFocus = document.getElementById('loginPhone');
      else if (step === 2) toFocus = document.getElementById('plate');
      if (toFocus) {
        try { /** @type {HTMLElement} */ (toFocus).focus(); }
        catch { /* ignore */ }
      } else if (step === 3) {
        const btn = document.getElementById('authButton');
        btn?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }
}

/**
 * Validate the inputs of the current step before allowing advance.
 * Returns true if OK. On failure, shows a MODAL alert and focuses the
 * offending field after the user dismisses it.
 *
 * New SMS-only wizard:
 *   step 1 = phone   (validate format)
 *   step 2 = terms   (validation happens at submit time, not on advance)
 *
 * The legacy step-2 (plate + year) validations remain below because the
 * DOM may still expose those fields for PWA autoSubmit / install-session;
 * `validateBeforeAdvance` only runs when the user clicks `data-wiz-next`
 * which the new 2-step wizard doesn't use beyond step 1.
 *
 * @param {number} step
 * @returns {Promise<boolean>}
 */
async function validateBeforeAdvance(step) {
  if (step === 1) {
    const phone = (/** @type {HTMLInputElement | null} */ (document.getElementById(IDS.loginPhone)))?.value ?? '';
    if (!isValidMobile(phone)) {
      await alert.showAlert({
        title: 'מספר טלפון לא תקין',
        message: 'הזן מספר ישראלי בפורמט 05XXXXXXXX או +9725XXXXXXXX.',
      });
      focusField(IDS.loginPhone);
      return false;
    }
    return true;
  }
  // Legacy step-2 (plate+year) — kept for the autoSubmit fallback path.
  if (step === 2 && document.getElementById(IDS.plate)) {
    const plate = (/** @type {HTMLInputElement | null} */ (document.getElementById(IDS.plate)))?.value ?? '';
    const year = (/** @type {HTMLSelectElement | null} */ (document.getElementById(IDS.carProductionYear)))?.value ?? '';
    if (!isValidPlate(plate)) {
      await alert.showAlert({
        title: 'מספר רכב לא תקין',
        message: 'נדרשות 7 או 8 ספרות. הקלד שוב את מספר הלוחית.',
      });
      focusField(IDS.plate);
      return false;
    }
    if (!/^\d{4}$/.test(year)) {
      await alert.showAlert({
        title: 'חסרה שנת ייצור',
        message: 'בחר את שנת הייצור של הרכב מתוך הרשימה.',
      });
      scrollToField(IDS.yearGrid);
      return false;
    }
    return true;
  }
  return true;
}

/**
 * Map an error message returned from auth.service.registerOrLogin() to the
 * wizard step that owns the offending field. Returns 0 (no navigation) for
 * generic/account errors that don't belong to a single step.
 *
 * NOTE: SMS-only wizard has 2 steps (phone → terms+submit). The legacy
 * 3rd step (plate+year) was removed; the plate/year errors only surface
 * via the PWA autoSubmit fallback path which calls `registerOrLogin`.
 * Terms errors now belong to step 2 (NOT step 3 — that pane no longer
 * exists; sending the user to step 3 leaves the screen blank because no
 * pane matches `data-pane="3"`).
 *
 * @param {string} message
 * @returns {1 | 2 | 0}
 */
function stepForErrorMessage(message) {
  const m = String(message || '');
  if (/טלפון/.test(m)) return 1;
  if (/הסכם|תנאי/.test(m)) return 2;
  if (/רכב|ייצור|בעלות|רישוי|לוחית/.test(m)) return 2;
  return 0;
}

/** @param {string} id */
function focusField(id) {
  const el = /** @type {HTMLInputElement | null} */ (document.getElementById(id));
  el?.focus();
  el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** @param {string} id */
function scrollToField(id) {
  const el = document.getElementById(id);
  el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** Fill the summary card on step 3 from the input values. */
function populateSummary() {
  const phone = (/** @type {HTMLInputElement | null} */ (document.getElementById(IDS.loginPhone)))?.value ?? '';
  const plate = (/** @type {HTMLInputElement | null} */ (document.getElementById(IDS.plate)))?.value ?? '';
  const year = (/** @type {HTMLSelectElement | null} */ (document.getElementById(IDS.carProductionYear)))?.value ?? '';
  const sp = document.getElementById(IDS.summaryPhone);
  const sl = document.getElementById(IDS.summaryPlate);
  const sy = document.getElementById(IDS.summaryYear);
  if (sp) sp.textContent = phone || '—';
  if (sl) sl.textContent = plate || '—';
  if (sy) sy.textContent = year || '—';

  // If OTP will be skipped for this phone (test-bypass phone OR a previously
  // trusted device), relabel the submit button "היכנס" instead of "שלח קוד
  // SMS" so the user isn't promised an SMS that will never arrive. Falls
  // back to the default label on error.
  void updateSubmitButtonLabel(phone);
}

/**
 * Decide whether SMS will be sent for this phone and relabel `#authButton`
 * accordingly. Two skip-OTP signals:
 *   1. Test-bypass phone — never sends SMS (the dedicated review phone).
 *   2. Trusted device   — `trustedDevices/{phone}/{deviceId}` exists from
 *      a previous successful OTP on this device.
 * Anything else (or any read error) → keep the default "send SMS" label.
 *
 * @param {string} rawPhone
 */
async function updateSubmitButtonLabel(rawPhone) {
  const btn = /** @type {HTMLButtonElement | null} */ (document.getElementById(IDS.authButton));
  if (!btn) return;

  // Default label whenever we can't determine skip-OTP — never promise the
  // user something we won't deliver.
  const SEND_SMS = 'שלח קוד SMS';
  const ENTER    = 'היכנס';

  const phone = normalizePhone(rawPhone || '');
  if (!isValidMobile(phone)) {
    setButtonLabel(btn, SEND_SMS);
    return;
  }

  // Test bypass — synchronous match, no DB hit.
  if (Auth.testBypass != null && Auth.testBypass.phone === phone) {
    setButtonLabel(btn, ENTER);
    return;
  }

  // Trusted device check — quick RTDB read.
  try {
    const trusted = await isDeviceTrustedForPhone(phone);
    setButtonLabel(btn, trusted ? ENTER : SEND_SMS);
  } catch {
    setButtonLabel(btn, SEND_SMS);
  }
}

/**
 * Replace the button's text content while preserving any trailing icon
 * (`<i class="fa-solid fa-arrow-left">`). The arrow is part of the visual
 * affordance and shouldn't disappear when we relabel.
 *
 * @param {HTMLButtonElement} btn
 * @param {string} text
 */
function setButtonLabel(btn, text) {
  const icon = btn.querySelector('i');
  btn.textContent = text + ' ';
  if (icon) btn.appendChild(icon);
}

// ====================================================================
// Plate live formatting (XX-XXX-XX / XXX-XX-XXX)
// ====================================================================

function wirePlateAutoFormat() {
  const input = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.plate));
  if (!input) return;
  input.addEventListener('input', () => {
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 8);
    input.value = formatPlate(digits);
    input.dataset.digits = digits;
  });
}

/** @param {string} digits */
function formatPlate(digits) {
  const d = String(digits || '').replace(/\D/g, '').slice(0, 8);
  if (d.length === 8) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  if (d.length === 7) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
  if (d.length >= 5) return `${d.slice(0, 2)}-${d.slice(2, 5)}${d.length > 5 ? '-' + d.slice(5) : ''}`;
  return d;
}

// ====================================================================
// Year chip grid — builds chips current → MIN_YEAR, syncs hidden <select>
// ====================================================================

function wireYearChips() {
  const grid = document.getElementById(IDS.yearGrid);
  const select = /** @type {HTMLSelectElement | null} */ (document.getElementById(IDS.carProductionYear));
  if (!grid || !select) return;

  // Build chips once
  if (!grid.dataset.populated) {
    const frag = document.createDocumentFragment();
    const current = new Date().getFullYear();
    for (let y = current; y >= MIN_YEAR; y--) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lw-year-chip';
      b.textContent = String(y);
      b.dataset.year = String(y);
      frag.appendChild(b);
    }
    grid.appendChild(frag);
    grid.dataset.populated = '1';
  }

  // Click → activate chip, set select, update label
  grid.addEventListener('click', (e) => {
    const chip = /** @type {HTMLElement} */ (e.target).closest?.('.lw-year-chip');
    if (!chip) return;
    grid.querySelectorAll('.lw-year-chip').forEach((c) => c.classList.toggle('active', c === chip));
    const y = /** @type {HTMLElement} */ (chip).dataset.year || '';
    select.innerHTML = `<option value="${y}" selected>${y}</option>`;
    select.value = y;
    const label = document.getElementById(IDS.yearValue);
    if (label) label.textContent = y;
    chip.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });
}

async function onSubmit() {
  // Guard against double-submit.
  if (submitting) return;
  submitting = true;

  clearAlert();
  const phone = (/** @type {HTMLInputElement | null} */ (document.getElementById(IDS.loginPhone)))?.value ?? '';
  const plateEl = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.plate));
  const yearEl  = /** @type {HTMLSelectElement | null} */ (document.getElementById(IDS.carProductionYear));
  const plate = plateEl?.value ?? '';
  const year  = yearEl?.value ?? '';
  const termsAccepted = (/** @type {HTMLInputElement | null} */ (document.getElementById(IDS.termsCheckbox)))?.checked ?? false;

  const btn = /** @type {HTMLButtonElement | null} */ (document.getElementById(IDS.authButton));
  const originalText = btn?.textContent ?? 'המשך';
  if (btn) {
    btn.disabled = true;
    btn.textContent = '⏳ מתחבר...';
  }

  const release = spinner.show('auth.submit', 'מתחבר... אנא המתן');

  // Path selection — when plate+year are present (PWA install-session
  // autofill, or any legacy caller), use the full `registerOrLogin` flow.
  // Otherwise use the new SMS-only `loginWithPhone`. This keeps the
  // trusted-device autoSubmit path 100% compatible while letting the new
  // 2-step wizard take the lighter path.
  const useLegacy = isValidPlate(plate) && /^\d{4}$/.test(year);

  let result;
  try {
    result = useLegacy
      ? await authService.registerOrLogin({ phone, plate, year, termsAccepted })
      : await authService.loginWithPhone({ phone, termsAccepted });
  } finally {
    release();
    if (btn) {
      btn.disabled = false;
      btn.textContent = originalText;
    }
    submitting = false;
  }

  if (result.status === 'logged-in') {
    log.info('login OK — uid=', result.uid, 'hasVehicle=', /** @type {any} */ (result).hasVehicle);
    /** @type {any} */ (window).showHomeScreen?.();
    return;
  }
  if (result.status === 'cancelled') return;
  if (result.status === 'error' && result.message) {
    const plainMessage = String(result.message).replace(/<[^>]+>/g, '');
    // Blocked accounts get the friendly designed popup with a mailto button,
    // matching the popup shown after attempts threshold in the vehicle flow.
    // The blocked phrase ("נחסם") is the canonical marker used by both the
    // attempts-threshold message and the admin-set block message.
    if (/נחסם/.test(plainMessage)) {
      const phoneInput = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.loginPhone));
      const typedPhone = phoneInput?.value?.trim();
      await showBlockedAccountModal({
        context: 'login',
        phone: typedPhone || undefined,
      });
      focusField(IDS.loginPhone);
      return;
    }
    await alert.showAlert({ title: 'לא ניתן להמשיך', message: plainMessage });
    const step = stepForErrorMessage(plainMessage);
    if (step && step !== currentStep) goToStep(step);
    if (step === 1) focusField(IDS.loginPhone);
    else if (step === 2) {
      // Step 2 in the SMS-only wizard is terms + submit; focus the
      // checkbox so the user sees what to act on. (Legacy plate field is
      // still in DOM as a hidden element for the autoSubmit fallback —
      // focusing it would do nothing visible.)
      focusField(IDS.termsCheckbox);
    }
  }
}

function clearAlert() {
  const el = document.getElementById(IDS.loginAlert);
  if (el) { el.innerHTML = ''; el.style.display = 'none'; }
}

/** @param {string} html */
function showAlert(html) {
  const el = document.getElementById(IDS.loginAlert);
  if (el) {
    el.innerHTML = html.replace(/\n/g, '<br>');
    el.style.display = 'block';
    el.setAttribute('role', 'alert');
    return;
  }
  // fallback when the legacy DOM isn't present yet (e.g. tests)
  alert.showAlert({ title: 'שגיאה', message: html.replace(/<[^>]+>/g, '') });
}

/** Convenience: populate + wire when DOM is ready. */
export function init() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    return;
  }
  populateYearSelect();
  wireForm();
}

/**
 * Pre-fill the login form's phone / plate / year fields from `LocalState`.
 * Safe no-op if any of the inputs aren't in the DOM yet. Called by the PWA
 * install-session bridge so the user doesn't have to re-type credentials
 * after the very first home-screen launch.
 */
export function prefillFromLocalState() {
  const phoneEl = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.loginPhone));
  const plateEl = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.plate));
  const yearEl  = /** @type {HTMLSelectElement | null} */ (document.getElementById(IDS.carProductionYear));
  const termsEl = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.termsCheckbox));
  if (phoneEl && LocalState.phone) phoneEl.value = LocalState.phone;
  if (plateEl && LocalState.plate) plateEl.value = LocalState.plate;
  if (yearEl  && LocalState.carYear) yearEl.value = String(LocalState.carYear);
  if (termsEl && LocalState.termsAccepted) termsEl.checked = true;
}

/**
 * Pre-fill the form and submit it automatically. Used by the PWA bootstrap
 * after the install-session bridge successfully claimed a token — at that
 * point `trustedDevices/{phone}/{deviceId}` is already in place server-side,
 * so the resulting `registerOrLogin` will skip OTP and complete silently.
 *
 * Idempotent — guarded by a module-level flag so a hot-refresh during dev
 * doesn't fire two submits.
 */
let autoSubmitFired = false;
export function autoSubmit() {
  if (autoSubmitFired) return;
  autoSubmitFired = true;
  // Make sure the form exists in the DOM before we touch it. The login
  // screen renders synchronously when showLoginScreen() is called, but
  // we still want a microtask boundary so any pending paint can settle.
  setTimeout(() => {
    prefillFromLocalState();
    void onSubmit();
  }, 50);
}
